/**
 * =============================================================================
 *  hover 預取的 debounce —— ROADMAP §6 交付項：「hover 預取（`prefetchQuery` on
 *  `onMouseEnter`，**debounce 120ms**）」。
 *
 *  120ms 買的是什麼
 *  ----------------
 *  側邊欄有 8–20 個項目。游標從最上面掃到最下面會經過每一個，
 *  沒有 debounce 就是 20 個 `/series` 請求同時出發、其中 19 個立刻沒有人要。
 *  那不只是浪費：它會把使用者**真的要去**的那一頁排在 19 個請求後面，
 *  於是「預取」讓頁面變慢了。
 *
 *  120ms 也是 `main.tsx` 給 router 的 `defaultPreloadDelay`。兩個數字相同是刻意的：
 *  路由的**程式碼**與頁面的**資料**應該在同一個時刻決定要不要拿——
 *  一個先一個後只會讓其中一個變成沒有用的那一半。
 *
 *  計時器注入，所以測得動（`metric-studio-prefetch.test.ts` 用假計時器斷言
 *  「掃過 20 個項目只發出 1 次預取」）。
 * ========================================================================== */

export const HOVER_PREFETCH_DELAY_MS = 120;

export interface TimerDeps {
  readonly setTimeout: (fn: () => void, ms: number) => number;
  readonly clearTimeout: (handle: number) => void;
}

const REAL_TIMERS: TimerDeps = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number,
  clearTimeout: (handle) => {
    globalThis.clearTimeout(handle);
  },
};

export interface HoverPrefetcher {
  /** `onMouseEnter` / `onFocus` 直接呼叫。`key` 相同時不會重複排程。 */
  readonly enter: (key: string, run: () => void) => void;
  /** `onMouseLeave` / `onBlur`。還沒到期的預取直接取消。 */
  readonly leave: () => void;
  /** 元件卸載時呼叫，否則計時器會在已經不存在的頁面上開火。 */
  readonly dispose: () => void;
  /** 稽核用：真正發出去的預取次數。 */
  readonly fired: () => number;
}

export function createHoverPrefetcher(
  delayMs: number = HOVER_PREFETCH_DELAY_MS,
  deps: TimerDeps = REAL_TIMERS,
): HoverPrefetcher {
  let handle: number | null = null;
  let pendingKey: string | null = null;
  let fired = 0;
  /** 已經預取過的 key 不再重複——快取已經有了，再發一次只是打後端。 */
  const done = new Set<string>();

  const cancel = (): void => {
    if (handle !== null) deps.clearTimeout(handle);
    handle = null;
    pendingKey = null;
  };

  return {
    enter: (key, run) => {
      if (done.has(key)) return;
      if (pendingKey === key && handle !== null) return;
      cancel();
      pendingKey = key;
      handle = deps.setTimeout(() => {
        handle = null;
        pendingKey = null;
        done.add(key);
        fired += 1;
        run();
      }, delayMs);
    },
    leave: cancel,
    dispose: () => {
      cancel();
      done.clear();
    },
    fired: () => fired,
  };
}
